import React from "react";
import { useTranslation } from "react-i18next";

import {
  compareSideColor,
  compareSideInk,
  type CompareSide,
} from "../processes/processCompareModel";

type RunCompareBandProps = {
  /** Which half of the comparison this is — it decides the band's colour. */
  side: CompareSide;
  /** The run's name, or the moment it ran — whatever the picker calls it. */
  label: string;
  /** Marks the run the session shows when no run is picked. */
  latest?: boolean;
  /** This side's run picker, so the band can be re-pointed from its own bar. */
  picker?: React.ReactNode;
  children: React.ReactNode;
};

/**
 * One run's half of a comparison: the tab's usual content under a bar naming
 * the run it belongs to.
 *
 * The bar sticks to the top of the viewport because the two halves are stacked
 * rather than side by side — on a long tab (the analysis report especially) the
 * reader is several screens from the heading that said which run this is, and
 * without the bar the only way to tell the halves apart is to scroll back.
 *
 * It is drawn in the side's own colour, the same pair the process comparison
 * uses: two bars a screen apart are told apart by hue long before their text is
 * read, and a tinted band with a solid rule under it is the boundary between
 * the halves — a hairline in the page's own grey was not one anybody found.
 */
const RunCompareBand = ({ side, label, latest = false, picker, children }: RunCompareBandProps) => {
  const { t } = useTranslation();
  const ink = compareSideInk(side);

  return (
    <section className="min-w-0">
      <div
        className="sticky top-0 z-30 mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-t-lg border-b-2 px-3 py-2 backdrop-blur"
        style={{
          borderBottomColor: ink,
          // Opaque enough to hide the content scrolling under it, and still the
          // side's own hue rather than a grey bar with coloured text on it.
          backgroundColor: `color-mix(in srgb, ${compareSideColor(side)} 18%, var(--color-background))`,
        }}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {/* The A/B letter at full strength, as on the process comparison: the
              one solid mark that lets the bar around it stay a tint. */}
          <span
            aria-hidden="true"
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] text-[11px] font-bold leading-none text-white"
            style={{ backgroundColor: ink }}
          >
            {side === "a" ? "A" : "B"}
          </span>
          <h2 className="min-w-0 truncate text-sm font-semibold" style={{ color: ink }}>
            {t("compare_run_band", { side: side === "a" ? "A" : "B", name: label })}
          </h2>
          {latest && (
            <span className="shrink-0 text-xs font-normal" style={{ color: ink, opacity: 0.75 }}>
              ({t("analysis_run_latest")})
            </span>
          )}
        </div>
        {picker && <div className="flex shrink-0 items-center">{picker}</div>}
      </div>
      {children}
    </section>
  );
};

export default RunCompareBand;
